import React from 'react';
import './../ff001-starstruck-lines/ff001-starstruck.css';
import Spin, { getValidAlarmPositions, IScenarioVaultReveal, ISpinInfo } from './spin/spin';
import request from 'superagent';
import { generateWindowFromReelStrips, getSymbolCountsFromWindow } from '../../helpers';

type TScenarioPreset = { _id: string; scenarios: IScenarioVaultReveal[] };

const cloneScenarios = (scenarios: IScenarioVaultReveal[]) =>
  JSON.parse(JSON.stringify(scenarios)) as IScenarioVaultReveal[];

const Model = (props: { modelId: any; }) => {
  const [modelId] = React.useState(props.modelId);
  const [render, setRender] = React.useState(false);
  const [config, setConfig] = React.useState<any>(null);
  const [userPublicKey, setUserPublicKey] = React.useState('');
  const [scenarios, setScenarios] = React.useState<IScenarioVaultReveal[]>([]);
  const [selectedScenario, setSelectedScenario] = React.useState('');
  const [scenarioList, setScenarioList] = React.useState<TScenarioPreset[]>([]);

  const generateDummyScenario = (config: any): IScenarioVaultReveal => ({
    reelIndexes: config.reelsSetup.map(() => 0),
  });

  const normalizeScenario = (scenario: Partial<IScenarioVaultReveal>, config: any): IScenarioVaultReveal => ({
    reelIndexes: config.reelsSetup.map((_: unknown, i: number) =>
      typeof scenario.reelIndexes?.[i] === 'number' ? scenario.reelIndexes[i] : 0
    ),
    forceAlarms: !!scenario.forceAlarms || getValidAlarmPositions(scenario.alarmPositions, config.reelsSetup).length > 0,
    alarmPositions: getValidAlarmPositions(scenario.alarmPositions, config.reelsSetup),
  });

  const getWindow = (scenario: IScenarioVaultReveal, reelStrips: string[][]) => {
    const reels = config.reelsSetup.length;
    const rows = config.reelsSetup[0];
    return generateWindowFromReelStrips({ reels, rows, reelIndexes: scenario.reelIndexes, reelStrips });
  };

  const getTriggerInfo = (counts: { [key: string]: number }) => ({
    scatTriggered: (counts[config.scat] || 0) >= config.countToTriggerFreeSpins,
    vaultTriggered: (counts[config.vaultAndReveal.symbol] || 0) >= config.countToTriggerVaultAndReveal,
  });

  const getSpinInfo = (index: number, scenarioArray = scenarios): ISpinInfo => {
    let freeSpinsCount = 0;
    let vaultActive: boolean = false;

    for (let i = 0; i <= index; i++) {
      const isFreeSpin = freeSpinsCount > 0;
      const vaultActiveBefore: boolean = vaultActive;
      const reelStrips = isFreeSpin
        ? vaultActiveBefore && config.vaultFreeSpinsReels?.length
          ? config.vaultFreeSpinsReels
          : config.freeSpinsReels
        : config.baseSpinsReels;
      const reelSetName = isFreeSpin
        ? vaultActiveBefore && config.vaultFreeSpinsReels?.length
          ? 'Vault Free Spins'
          : 'Free Spins'
        : 'Base';
      const window = getWindow(scenarioArray[i], reelStrips);
      const counts = getSymbolCountsFromWindow(window);
      const { scatTriggered, vaultTriggered } = getTriggerInfo(counts);
      let freeSpinsAfter = isFreeSpin ? Math.max(freeSpinsCount - 1, 0) : 0;
      if (scatTriggered) { freeSpinsAfter += config.freeSpinsWinCount; }
      if (vaultTriggered) { freeSpinsAfter += config.vaultAndRevealWinCount; }

      const revealOnThisSpin = isFreeSpin && vaultActiveBefore && freeSpinsAfter === 0;
      const info = {
        reelSetName,
        reelStrips,
        isFreeSpin,
        vaultActive: vaultActiveBefore || vaultTriggered,
        revealOnThisSpin,
        freeSpinsBefore: freeSpinsCount,
        freeSpinsAfter,
        counts,
      };
      if (i === index) { return info; }

      freeSpinsCount = freeSpinsAfter;
      vaultActive = (vaultActiveBefore || vaultTriggered) && freeSpinsAfter > 0;
    }

    throw new Error('Spin info index out of range');
  };

  const getRemainingFreeSpinsAfterLastScenario = () => {
    if (!config || scenarios.length === 0) { return 0; }
    return getSpinInfo(scenarios.length - 1).freeSpinsAfter;
  };

  const changeUserPublicKey = (txt: string) => {
    setUserPublicKey(txt);
    localStorage.setItem('userid', txt);
  };

  const getLabelForScenario = (scenario: IScenarioVaultReveal[]) => {
    if (!config || scenario.length === 0) { return 'sc.'; }
    const normalized = scenario.map(it => normalizeScenario(it, config));
    const spin = getSpinInfo(0, normalized);
    const counts = spin.counts;
    let str = 'sc. ';
    (counts[config.scat] || 0) >= config.countToTriggerFreeSpins && (str += `${counts[config.scat]} scat, `);
    (counts[config.vaultAndReveal.symbol] || 0) >= config.countToTriggerVaultAndReveal && (str += `${counts[config.vaultAndReveal.symbol]} vault, `);
    (counts[config.wild] || 0) > 0 && (str += `${counts[config.wild]} wild, `);
    str += `${scenario.length} spin${scenario.length === 1 ? '' : 's'}`;
    return str;
  };

  const changeScenarioFromBelow = (scenario: IScenarioVaultReveal, index: number) => {
    scenarios[index] = normalizeScenario(scenario, config);
    setScenarios([...scenarios]);
  };

  const newScenario = () => {
    setSelectedScenario('');
    setScenarios([{ ...generateDummyScenario(config) }]);
  };

  const addNewFreespin = () => {
    scenarios.push({ ...generateDummyScenario(config) });
    setScenarios([...scenarios]);
  };

  const addRemainingFreespins = () => {
    const remaining = getRemainingFreeSpinsAfterLastScenario();
    if (remaining <= 0) { return; }
    setScenarios([
      ...scenarios,
      ...Array.from({ length: remaining }).map(() => ({ ...generateDummyScenario(config) })),
    ]);
  };

  const removeFreeSpin = () => {
    if (scenarios.length === 1) { return; }
    scenarios.pop();
    setScenarios([...scenarios]);
  };

  const save = async () => {
    const payload = {
      scenarios,
      modelId,
      _id: selectedScenario === '' ? undefined : selectedScenario,
    };
    const res = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/upsert`).withCredentials().send(payload))?.body;
    if (!res.success) { return alert('Error saving scenario'); }
    console.log('Scenario saved');
  };

  const del = async () => {
    if (!window.confirm('Confirm deletion')) { return; }
    const payload = {
      scenarios,
      _id: selectedScenario,
    };
    const res = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/delete`).withCredentials().send(payload))?.body;
    if (!res.success) { return alert('Error deleting scenario'); }
    alert('Scenario deleted');
    window.location.reload();
  };

  const play = async () => {
    if (userPublicKey.trim() === '') { return alert('Please enter a user id'); }
    const payload: { userPublicKey: string; modelId: string; scenarios: IScenarioVaultReveal[] } = {
      scenarios,
      modelId,
      userPublicKey,
    };
    const res = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/play-user`).withCredentials().send(payload))?.body;
    if (!res.success) { return alert('Error saving scenario'); }
    console.log('Scenario saved');
  };

  React.useEffect(() => {
    if (!config) { return; }
    setRender(true);
  }, [scenarios, config]);

  React.useEffect(() => {
    const ss = scenarioList.find(it => it._id === selectedScenario)?.scenarios;
    if (selectedScenario === '' || !ss || !config) { return; }
    const newS = cloneScenarios(ss).map(it => normalizeScenario(it, config));
    console.log(JSON.stringify(newS));
    setScenarios(newS.length > 0 ? newS : [{ ...generateDummyScenario(config) }]);
  }, [selectedScenario]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    localStorage.getItem('userid') && setUserPublicKey(`${localStorage.getItem('userid')}`);
    let req: request.SuperAgentRequest | undefined;
    req = request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get-config`).withCredentials().send({ modelId });
    req.then(async (configRes) => {
      const config = configRes.body.config;
      setConfig(config);

      req = request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get`).withCredentials().send({ modelId });
      req.then(async (scenariosRes) => {
        if (!scenariosRes.body.success) { return alert('Error loading scenarios'); }
        req = undefined;
        const scenarioPresets = scenariosRes.body.scenarios;
        setScenarioList(scenarioPresets);
        setScenarios([{ ...generateDummyScenario(config) }]);
        setRender(true);
        console.log('scenarios', scenarioPresets);
      }).catch(e => console.error(e));
    }).catch(e => console.error(e));
    return () => {
      try { req?.abort(); } catch (e) { console.log('Req aborted'); }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remainingFreeSpins = getRemainingFreeSpinsAfterLastScenario();

  return !render ? <></> : (
    <div className="ff001-container">
      <h1>{modelId}</h1>
      <div className='scenario-presets'>
        <h3 style={{ marginBottom: 5 }}>Scenario presets</h3>
        <div className='ff001-scenario-select'>
          <select value={selectedScenario} onChange={e => setSelectedScenario(e.target.value)} >
            <option label={''} value={''} />
            {scenarioList.map((ps, i) => <option key={`ps-${i}`} value={`${ps._id}`} label={getLabelForScenario(ps.scenarios)} />)}
          </select>
        </div>
      </div>

      {scenarios.map((scenario, i) => (
        <Spin
          key={`${selectedScenario}-${i}-${getSpinInfo(i).reelSetName}`}
          config={config}
          scenario={scenario}
          index={i}
          spinInfo={getSpinInfo(i)}
          onScenarioUpdated={(scenario, i) => changeScenarioFromBelow(scenario, i)}
        />))}

      <div className='btn-freespins-group' style={{ flexWrap: 'wrap', gap: 4 }}>
        <div className={'btn-freespins-action'} onClick={() => addNewFreespin()}>Add freespin</div>
        <div className={'btn-freespins-action'} onClick={() => removeFreeSpin()}>remove freespin</div>
        {remainingFreeSpins > 0 && <div className={'btn-freespins-action'} onClick={() => addRemainingFreespins()}>Add remaining ({remainingFreeSpins})</div>}
      </div>
      <div className='ff001-sep'>&nbsp;</div>
      <div className={`ff001-userid`}>
        <h3 style={{ marginRight: 8 }}>User Public Key</h3>
        <div>
          <input type={'text'} value={userPublicKey} onChange={(e) => changeUserPublicKey(e.currentTarget.value)} />
        </div>
      </div>
      <div style={{ fontSize: 8 }}>To find it: Go to the game, network tab, filter by "init", under Preview, user then id </div>

      <div className='model-buttons'>
        <div className={'btn-save'} onClick={() => newScenario()}>New</div>
        <div className={'btn-save'} onClick={() => save()}>Save</div>
        {selectedScenario !== '' && <div className={'btn-save'} onClick={() => del()}>Delete</div>}
        <div className={'btn-save btn-play'} onClick={() => play()}>Play</div>
      </div>
    </div>
  );
};

export default Model;
